<template>
  <!-- Lisa uus kasutaja / Muuda in the hooldusplaat look (Seaded -> Kasutajad
       uues kujunduses): the person, their access, and their colour, with a
       card showing them as the Kalender will draw them — and whose colour it
       already is — plus how the account stands and Salvesta. -->
  <div class="uf">
    <div class="uf-cap">Kasutajad</div>
    <h1 class="uf-title">{{ editing ? 'Muuda kasutajat' : 'Lisa uus kasutaja' }}<span v-if="editing && original" class="uf-title-sub"> · {{ original.fullName }}</span></h1>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" />
    <form v-else class="uf-layout" novalidate @submit.prevent="save()">
      <div>
        <section class="uf-sec">
          <h2><span class="uf-num">1</span>Isik</h2>
          <div class="uf-g2">
            <label class="uf-fld"><span class="uf-lbl">Eesnimi <span class="uf-req">*</span></span>
              <input v-model.trim="form.firstName" class="uf-in" :class="{ bad: tried && !form.firstName }" autocomplete="off"></label>
            <label class="uf-fld"><span class="uf-lbl">Perenimi <span class="uf-req">*</span></span>
              <input v-model.trim="form.lastName" class="uf-in" :class="{ bad: tried && !form.lastName }" autocomplete="off"></label>
            <label class="uf-fld uf-full"><span class="uf-lbl">E-post <span class="uf-req">*</span> <span class="uf-hint">sellega logitakse sisse</span></span>
              <input v-model.trim="form.email" type="email" class="uf-in" :class="{ bad: tried && !form.email }" autocomplete="off"></label>
            <label class="uf-fld"><span class="uf-lbl">Telefon 1</span><input v-model.trim="form.phone1" type="tel" class="uf-in" autocomplete="off"></label>
            <label class="uf-fld"><span class="uf-lbl">Telefon 2</span><input v-model.trim="form.phone2" type="tel" class="uf-in" autocomplete="off"></label>
          </div>
        </section>

        <section class="uf-sec">
          <h2><span class="uf-num">2</span>Ligipääs</h2>
          <div class="uf-lbl uf-mb">Rollid</div>
          <div class="uf-roles" role="group" aria-label="Rollid">
            <button
              v-for="role in roleChoices"
              :key="role.id"
              type="button"
              class="uf-role"
              :aria-pressed="String(form.roles.includes(role.id))"
              @click="toggleRole(role.id)"
            >
              <span class="uf-box" aria-hidden="true" />
              <span><b>{{ role.text }}</b><span>{{ role.hint }}</span></span>
            </button>
          </div>
          <p v-if="!form.roles.length" class="uf-warn-line">Ilma rollita saab ainult sisse logida ja oma seadeid muuta.</p>

          <div class="uf-switches">
            <button type="button" class="uf-switch" role="switch" :aria-checked="String(!!form.active)" @click="form.active = !form.active">
              <span class="uf-track" aria-hidden="true" /><span><b>Aktiivne</b><span>Mitteaktiivne ei saa sisse logida ega ole planeerijas.</span></span>
            </button>
            <button type="button" class="uf-switch" role="switch" :aria-checked="String(!!form.showInPlanner)" @click="form.showInPlanner = !form.showInPlanner">
              <span class="uf-track" aria-hidden="true" /><span><b>Planeerijas</b><span>Tal on Planeerijas oma rida.</span></span>
            </button>
          </div>

          <label class="uf-fld uf-pass">
            <span class="uf-lbl">{{ editing ? 'Uus parool' : 'Parool' }} <span v-if="!editing" class="uf-req">*</span>
              <span class="uf-hint">{{ editing ? 'tühjaks jättes jääb praegune' : 'vähemalt 6 tähemärki' }}</span></span>
            <input v-model="form.password" type="password" class="uf-in" :class="{ bad: tried && passwordError }" autocomplete="new-password">
            <span v-if="tried && passwordError" class="uf-err">{{ passwordError }}</span>
          </label>
        </section>

        <section class="uf-sec">
          <h2><span class="uf-num">3</span>Värv <span class="uf-hint uf-h2hint">nii on ta Kalendris ja Planeerijas</span></h2>
          <div class="uf-colors" role="group" aria-label="Värv">
            <button
              v-for="color in palette"
              :key="color"
              type="button"
              class="uf-swatch"
              :style="{ background: color }"
              :aria-pressed="String(same(form.color, color))"
              :aria-label="color"
              :title="takenBy(color).length ? color + ' · ' + takenBy(color).join(', ') : color"
              @click="form.color = color"
            ><span v-if="takenBy(color).length" class="uf-taken" aria-hidden="true" /></button>
            <label class="uf-own">
              <input v-model="form.color" type="color" aria-label="Vali ise">
              <span>Vali ise</span>
            </label>
            <input v-model.trim="form.color" class="uf-in uf-hex" aria-label="Värvi kood" maxlength="7">
          </div>
          <p class="uf-hint uf-mt">Täpiga värv on juba kellelgi aktiivsel.</p>
        </section>
      </div>

      <aside class="uf-card">
        <div class="uf-card-head">
          <div class="uf-person">
            <span class="uf-ini" :style="dot(form.color)">{{ initials(fullName || '?') }}</span>
            <div>
              <div class="uf-card-name" :class="{ empty: !fullName }">{{ fullName || 'Nimi puudub' }}</div>
              <div class="uf-hint">{{ roleLine }}</div>
            </div>
          </div>
        </div>
        <div class="uf-preview">
          <div class="uf-k">KALENDRIS ({{ lookText }})</div>
          <span v-if="look.value === 'vana'" class="uf-vana" :style="{ background: form.color }">
            <b>09:00–10:30</b><br>{{ fullName || 'Nimi' }}<br>Kase 12, Tallinn
          </span>
          <span v-else class="uf-pre cal-plate" :class="'cal-look-' + look.value">
            <span class="cal-ev" :style="orderStyle(look, form.color)">
              <span class="event-line cal-ev__time" :style="timeStyle(look, form.color)">
                <span class="cal-ini" :class="{ 'cal-ini--on': look.fullTime }" :style="initialsStyle(look, form.color)">{{ initials(fullName || '?') }}</span>
                09:00–10:30
              </span>
              <span v-if="look.nameLine" class="event-line">{{ fullName || 'Nimi' }}</span>
              <span class="event-line cal-adr">Kase 12, Tallinn</span>
              <span class="event-line">Mart Saar</span>
            </span>
          </span>
          <p v-if="clash.length" class="uf-clash">Sama värv on ka: <b>{{ clash.join(', ') }}</b></p>
        </div>

        <dl v-if="editing && original" class="uf-card-body">
          <div class="uf-row"><dt>Viimati sees</dt><dd>{{ original.lastLogin ? dateTime(original.lastLogin) : 'pole loginud' }}</dd></div>
          <div class="uf-row"><dt>Allkiri</dt><dd>{{ original.hasSignature ? 'salvestatud' : 'puudub' }}</dd></div>
          <div class="uf-row"><dt>Google</dt><dd>{{ original.hasGoogleToken ? 'ühendatud' : 'ühendamata' }}</dd></div>
          <div v-if="original.isLocked" class="uf-row"><dt>Konto</dt><dd class="uf-bad">{{ original.locked ? 'Lukus (püsiv)' : 'Lukus' }}
            <button type="button" class="uf-link" :disabled="busy" @click="unlock()">Ava lukk</button></dd></div>
        </dl>
        <passkeys-of v-if="editing && original" kind="user" :owner-id="userId" class="uf-card-body" />

        <div class="uf-card-foot">
          <div v-if="tried && missing.length" class="uf-err">Puudu: {{ missing.join(', ') }}</div>
          <button type="submit" class="uf-btn" :disabled="saving">{{ saving ? 'Salvestan…' : 'Salvesta' }}</button>
          <router-link :to="{ name: 'user/list' }" class="uf-link">Tagasi kasutajate juurde</router-link>
        </div>
      </aside>
    </form>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'
import loadArchivo from '@/plugins/archivo'
import PasskeysOf from '@/components/PasskeysOf.vue'
import { initials } from '@/components/frame/frameLinks'
import { effectiveLook, lookOf, orderStyle, timeStyle, initialsStyle } from '@/plugins/calendarLooks'

const ROLE_INFO = {
  tehnic: { text: 'Tehnik', hint: 'Töökäsud, aktid, oma kalender.' },
  secretary: { text: 'Sekretär', hint: 'Kliendid, objektid ja kõigi kalender.' },
  admin: { text: 'Administraator', hint: 'Kõik kontoritööd, kasutajad, hooldus.' },
  root: { text: 'Süsteemi haldur', hint: 'Süsteemi seaded, varundus, moodulid.' }
}
const ROLE_ORDER = ['tehnic', 'secretary', 'admin', 'root']

// Colours that stay apart on the Kalender and read with white or dark text.
const PALETTE = [
  '#2E7D32', '#8AC25D', '#00838F', '#1F5FD1', '#443C85', '#6A4C93', '#AD1457', '#C62828',
  '#E65100', '#F9A825', '#795548', '#455A64', '#26A69A', '#5C6BC0', '#EC407A', '#9E9D24'
]

const EMPTY = {
  firstName: '', lastName: '', email: '', password: '', phone1: '', phone2: '',
  color: '#2E7D32', roles: [], active: true, showInPlanner: false
}

export default {
  components: { PasskeysOf },
  data () {
    return {
      loading: false,
      saving: false,
      busy: false,
      tried: false,
      form: { ...EMPTY, roles: [] },
      original: null,
      roles: [],
      others: [],
      palette: PALETTE
    }
  },
  computed: {
    ...mapState(['features', 'prefs']),
    userId () {
      return this.$route.params.userId
    },
    editing () {
      return !!this.userId
    },
    fullName () {
      return [this.form.firstName, this.form.lastName].filter(Boolean).join(' ')
    },
    roleChoices () {
      // Süsteemi haldur is root's to give (the server refuses anyone else).
      return ROLE_ORDER.map(title => this.roles.find(role => role.title === title))
        .filter(Boolean)
        .filter(role => role.title !== 'root' || this.$hasRole(['root']) || (this.form.roles || []).includes(role.id))
        .map(role => ({ id: role.id, title: role.title, ...ROLE_INFO[role.title] }))
    },
    roleLine () {
      const titles = this.roles.filter(role => this.form.roles.includes(role.id)).map(role => role.title)
      const text = ROLE_ORDER.filter(t => titles.includes(t)).map(t => ROLE_INFO[t].text).join(', ') || 'Rollita'
      return text + (this.form.active ? '' : ' · mitteaktiivne')
    },
    look () {
      return lookOf(effectiveLook(this.prefs, this.features))
    },
    lookText () {
      return this.look.text.toLowerCase()
    },
    clash () {
      return this.takenBy(this.form.color)
    },
    passwordError () {
      const password = this.form.password || ''
      if (!this.editing && !password) return 'Parool on kohustuslik'
      if (password && password.length < 6) return 'Parool peab olema vähemalt 6 tähemärki pikk'
      return ''
    },
    missing () {
      return [
        !this.form.firstName && 'eesnimi',
        !this.form.lastName && 'perenimi',
        !this.form.email && 'e-post',
        this.passwordError && 'parool'
      ].filter(Boolean)
    }
  },
  watch: {
    userId () {
      this.load()
    }
  },
  created () {
    loadArchivo()
    this.load()
  },
  methods: {
    ...mapActions(['showNotification']),
    initials,
    orderStyle,
    timeStyle,
    initialsStyle,
    async load () {
      this.loading = true
      this.tried = false
      try {
        const [{ roles }, { users }] = await Promise.all([api.getRoles(), api.getUsers({ limit: 500, active: 1 })])
        this.roles = roles
        this.others = users
        if (this.editing) {
          const user = await api.getUser(this.userId)
          this.original = user
          this.form = {
            ...EMPTY,
            ...Object.fromEntries(Object.keys(EMPTY).map(key => [key, user[key] == null ? EMPTY[key] : user[key]])),
            password: '',
            roles: (user.roles || []).map(role => role.id)
          }
        } else {
          this.original = null
          this.form = { ...EMPTY, roles: [] }
        }
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    toggleRole (id) {
      const on = this.form.roles.includes(id)
      this.form.roles = on ? this.form.roles.filter(r => r !== id) : this.form.roles.concat(id)
      // A new technician shows in the Planeerija (mirrors migration 03), but
      // only when the role is freshly added on a new user, so editing never
      // overrides what is stored.
      const tehnic = this.roles.find(role => role.title === 'tehnic')
      if (!on && !this.editing && tehnic && id === tehnic.id) this.form.showInPlanner = true
    },
    norm (color) {
      return String(color || '').trim().toLowerCase().replace(/^#?/, '#').slice(0, 7)
    },
    same (a, b) {
      return this.norm(a) === this.norm(b)
    },
    // Active people other than this one who have the colour.
    takenBy (color) {
      return this.others.filter(user => String(user.id) !== String(this.userId) && this.same(user.color, color)).map(user => user.fullName)
    },
    dot (color) {
      const hex = this.norm(color).slice(1)
      if (!/^[0-9a-f]{6}$/.test(hex)) return { background: '#5B6B66', color: '#fff' }
      const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
      const light = (0.299 * r + 0.587 * g + 0.114 * b) > 150
      return { background: '#' + hex, color: light ? '#16322A' : '#fff' }
    },
    dateTime (date) {
      return dayjs(date).format('DD.MM.YYYY HH:mm')
    },
    async save () {
      this.tried = true
      if (this.missing.length || this.saving) return
      this.saving = true
      const body = { ...this.form, color: this.norm(this.form.color) }
      // An empty password on an edit keeps the current one.
      if (!body.password) delete body.password
      try {
        if (this.editing) {
          await api.updateUser(this.userId, body)
          this.showNotification({ text: 'Kasutaja edukalt uuendatud', color: 'success' })
          this.load()
        } else {
          const { user } = await api.createUser(body)
          this.showNotification({ text: 'Uus kasutaja edukalt loodud', color: 'success' })
          this.$router.push({ name: 'user', params: { userId: user.id } })
        }
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = false
      }
    },
    async unlock () {
      this.busy = true
      try {
        await api.unlockUser(this.userId)
        this.showNotification({ text: 'Konto lukk eemaldatud', color: 'success' })
        this.original = await api.getUser(this.userId)
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.uf {
  --f-surface: #FFFFFF; --f-ink: #16322A; --f-ink-2: #3E504A; --f-ink-3: #5B6B66;
  --f-line: #C9D2CE; --f-line-soft: #E1E7E4; --f-green: #2E7D32; --f-green-dark: #1B5E20; --f-green-bg: #EAF3EA;
  --f-blue: #1F5FD1; --f-amber: #8F5400; --f-amber-bg: #FFF4E0; --f-red: #B3261E;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--f-ink); max-width: 1180px; padding-bottom: 40px;
}
.uf button, .uf input { font: inherit; color: inherit; }
.uf :focus-visible { outline: 3px solid var(--f-blue); outline-offset: 2px; }
.uf-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--f-ink-3); font-weight: 600; }
.uf-title { font-size: 30px; font-weight: 600; margin: 2px 0 16px; letter-spacing: -.01em; line-height: 1.2; }
.uf-title-sub { color: var(--f-ink-3); font-weight: 400; }
.uf-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 18px; align-items: start; }
.uf-sec { background: var(--f-surface); border: 1px solid var(--f-line-soft); border-radius: 10px; padding: 16px; margin-bottom: 14px; }
.uf-sec h2 { font-size: 16px; font-weight: 600; margin: 0 0 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.uf-num { width: 22px; height: 22px; border-radius: 50%; background: var(--f-ink); color: #fff; font-size: 12px; display: grid; place-items: center; }
.uf-h2hint { font-weight: 400; }
.uf-g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: start; }
.uf-fld { display: grid; gap: 4px; min-width: 0; }
.uf-full { grid-column: 1 / -1; }
.uf-lbl { font-size: 12px; color: var(--f-ink-3); font-weight: 600; letter-spacing: .02em; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.uf-req { color: var(--f-red); }
.uf-in { border: 1px solid var(--f-line); border-radius: 8px; padding: 9px 11px; background: var(--f-surface); width: 100%; min-width: 0; }
.uf-in:focus { outline: 2px solid var(--f-blue); outline-offset: 0; border-color: var(--f-blue); }
.uf-in.bad { border-color: var(--f-red); }
.uf-hint { font-size: 12px; color: var(--f-ink-3); font-weight: 400; letter-spacing: 0; }
.uf-err { font-size: 12px; color: var(--f-red); }
.uf-mb { margin-bottom: 6px; }
.uf-mt { margin: 8px 0 0; }

.uf-roles { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.uf-role { display: flex; gap: 10px; align-items: flex-start; text-align: left; border: 1px solid var(--f-line); background: var(--f-surface); border-radius: 10px; padding: 10px 12px; cursor: pointer; }
.uf-role b { display: block; font-size: 14.5px; }
.uf-role span span { font-size: 12.5px; color: var(--f-ink-3); }
.uf-box { width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--f-line); flex: none; margin-top: 1px; }
.uf-role[aria-pressed="true"] { border-color: var(--f-green); box-shadow: inset 0 0 0 1px var(--f-green); background: #F2F8F2; }
.uf-role[aria-pressed="true"] .uf-box { border-color: var(--f-green); background: var(--f-green); box-shadow: inset 0 0 0 2px #fff; }
.uf-warn-line { margin: 8px 0 0; font-size: 12.5px; color: var(--f-amber); }
.uf-switches { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 14px; }
.uf-switch { display: flex; gap: 10px; align-items: flex-start; border: 0; background: none; cursor: pointer; padding: 4px 0; text-align: left; }
.uf-switch b { display: block; font-size: 14.5px; }
.uf-switch span span { font-size: 12.5px; color: var(--f-ink-3); }
.uf-track { width: 38px; height: 22px; border-radius: 999px; background: var(--f-line); position: relative; flex: none; transition: background .15s; }
.uf-track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .15s; }
.uf-switch[aria-checked="true"] .uf-track { background: var(--f-green); }
.uf-switch[aria-checked="true"] .uf-track::after { transform: translateX(16px); }
.uf-pass { margin-top: 14px; max-width: 340px; }

.uf-colors { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.uf-swatch { width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px var(--f-line); cursor: pointer; position: relative; padding: 0; }
.uf-swatch[aria-pressed="true"] { box-shadow: 0 0 0 2px var(--f-ink); }
.uf-taken { position: absolute; right: -2px; top: -2px; width: 10px; height: 10px; border-radius: 50%; background: #fff; box-shadow: inset 0 0 0 3px var(--f-ink-2); }
.uf-own { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--f-ink-2); cursor: pointer; margin-left: 6px; }
.uf-own input { width: 34px; height: 34px; padding: 0; border: 1px solid var(--f-line); border-radius: 8px; background: none; cursor: pointer; }
.uf-hex { width: 96px; font-variant-numeric: tabular-nums; }

.uf-card { position: sticky; top: calc(var(--v-layout-top, 0px) + 16px); background: var(--f-surface); border: 1px solid var(--f-line-soft); border-radius: 10px; overflow: hidden; }
.uf-card-head { padding: 14px 16px; border-bottom: 1px solid var(--f-line-soft); }
.uf-person { display: flex; gap: 12px; align-items: center; }
.uf-ini { width: 44px; height: 44px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; flex: none; }
.uf-card-name { font-size: 18px; font-weight: 600; overflow-wrap: anywhere; }
.uf-card-name.empty { color: var(--f-ink-3); font-weight: 400; }
.uf-preview { padding: 12px 16px; border-bottom: 1px solid var(--f-line-soft); display: grid; gap: 8px; }
.uf-k { font-size: 10.5px; font-weight: 700; letter-spacing: .07em; color: var(--f-ink-3); }
.uf-clash { margin: 0; font-size: 12.5px; color: var(--f-amber); background: var(--f-amber-bg); border-radius: 6px; padding: 6px 8px; }
/* A strip of Kalender with one order in it, as Minu seaded draws its looks. */
.uf-pre { display: block; padding: 6px; border-radius: 8px; background: #F4F6F5; pointer-events: none; }
.uf-pre .cal-ev { display: block; overflow: hidden; min-width: 0; min-height: 76px; }
.uf-pre .event-line { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.uf-pre .cal-ev__time { display: flex; }
.uf-vana { display: block; color: #fff; font-family: Roboto, sans-serif; font-size: 12px; line-height: 15px; padding: 6px; border-radius: 4px; }
.uf-card-body { padding: 12px 16px; display: grid; gap: 6px; font-size: 13px; margin: 0; border-bottom: 1px solid var(--f-line-soft); }
.uf-row { display: grid; grid-template-columns: 90px minmax(0, 1fr); gap: 8px; }
.uf-row dt { color: var(--f-ink-3); }
.uf-row dd { margin: 0; overflow-wrap: anywhere; }
.uf-bad { color: var(--f-red); font-weight: 600; display: flex; gap: 10px; flex-wrap: wrap; }
.uf-card-foot { padding: 12px 16px; display: grid; gap: 8px; background: #F8FAF9; }
.uf .uf-btn { height: 40px; border-radius: 6px; border: 0; background: var(--f-green); color: #fff; font-weight: 700; font-size: 15px; cursor: pointer; }
.uf .uf-btn:disabled { opacity: .6; cursor: default; }
.uf .uf-link { border: 0; background: none; padding: 0; color: var(--f-green-dark); font-weight: 600; font-size: 13px; text-decoration: none; cursor: pointer; justify-self: start; }

@media (max-width: 1000px) { .uf-layout { grid-template-columns: minmax(0, 1fr); } .uf-card { position: static; } }
@media (max-width: 760px) { .uf-g2, .uf-roles, .uf-switches { grid-template-columns: minmax(0, 1fr); } .uf-title { font-size: 24px; } }
</style>
